import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, Copy, CheckCircle2, UserCircle, HeartPulse, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { useGym } from '@/contexts/GymContext';

export default function OwnerCustomers() {
    const navigate = useNavigate();
    const { selectedGym, setSelectedGym } = useGym();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ first_name: '', last_name: '', dni: '', phone: '', email: '', birth_date: '', notes: '' });
    const [createAccount, setCreateAccount] = useState(false);
    const [accountPassword, setAccountPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [listError, setListError] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [copied, setCopied] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [generatingLink, setGeneratingLink] = useState(false);
    const [editingCode, setEditingCode] = useState(false);
    const [codeInput, setCodeInput] = useState('');
    const [savingCode, setSavingCode] = useState(false);
    const [codeError, setCodeError] = useState('');

    const inviteUrl = inviteCode ? `${window.location.origin}/invitar/${inviteCode}` : '';

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        setListError('');
        try {
            const res = await api.get('/owner/customers', { params: { search, page, per_page: 10 } });
            setCustomers(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch (err) {
            setListError(err.response?.data?.message || 'Error al cargar los alumnos.');
        } finally { setLoading(false); }
    }, [search, page]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    useEffect(() => {
        const fetchInviteCode = async () => {
            try {
                const res = await api.get('/owner/gym');
                setInviteCode(res.data.data?.invite_token || '');
            } catch {
                // Gym may not exist yet; invite panel stays hidden.
            }
        };
        fetchInviteCode();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ first_name: '', last_name: '', dni: '', phone: '', email: '', birth_date: '', notes: '' });
        setCreateAccount(false);
        setAccountPassword('');
        setError('');
        setDialogOpen(true);
    };

    const openEdit = (customer) => {
        setEditing(customer);
        setForm({
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            dni: customer.dni || '',
            phone: customer.phone || '',
            email: customer.email || '',
            birth_date: customer.birth_date?.split('T')[0] || '',
            notes: customer.notes || '',
        });
        setCreateAccount(false);
        setAccountPassword('');
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const data = { ...form };
            if (!data.birth_date) delete data.birth_date;
            if (editing) {
                await api.put(`/owner/customers/${editing.id}`, data);
            } else {
                if (createAccount) {
                    data.create_account = true;
                    data.account_password = accountPassword;
                }
                await api.post('/owner/customers', data);
            }
            setDialogOpen(false);
            fetchCustomers();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleCreateAccount = async (customer) => {
        const password = prompt('Ingresá una contraseña para la cuenta del alumno (mín. 8 caracteres):');
        if (!password) return;
        if (password.length < 8) {
            alert('La contraseña debe tener al menos 8 caracteres.');
            return;
        }
        try {
            await api.post(`/owner/customers/${customer.id}/create-account`, { password });
            alert('Cuenta de acceso creada exitosamente.');
            fetchCustomers();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al crear la cuenta.');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este alumno?')) return;
        try {
            await api.delete(`/owner/customers/${id}`);
            fetchCustomers();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar el alumno.');
        }
    };

    const regenerateInvite = async () => {
        if (!confirm('¿Regenerar el código? Los links y códigos anteriores dejarán de funcionar.')) return;
        setGeneratingLink(true);
        setCodeError('');
        try {
            const res = await api.post('/owner/gym/regenerate-invite');
            syncInviteCode(res.data.data.invite_token);
        } catch (err) {
            setCodeError(err.response?.data?.message || 'Error al regenerar el código.');
        } finally {
            setGeneratingLink(false);
        }
    };

    const startEditCode = () => {
        setCodeInput(inviteCode);
        setCodeError('');
        setEditingCode(true);
    };

    const saveInviteCode = async () => {
        setSavingCode(true);
        setCodeError('');
        try {
            const res = await api.put('/owner/gym/invite-token', { token: codeInput });
            syncInviteCode(res.data.data.invite_token);
        } catch (err) {
            const errors = err.response?.data?.errors;
            setCodeError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Error al guardar el código.'));
        } finally {
            setSavingCode(false);
        }
    };

    const syncInviteCode = (token) => {
        setInviteCode(token);
        setEditingCode(false);
        if (selectedGym) {
            setSelectedGym({ ...selectedGym, invite_token: token });
        }
    };

    const copyText = async (text, setFlag) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        setFlag(true);
        setTimeout(() => setFlag(false), 2000);
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Alumnos</h1>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nuevo Alumno</Button>
                </div>
            </div>

            {inviteCode && (
                <div className="mb-6 p-4 rounded-lg border border-[#3a3a3f] bg-[#0a0a0a] space-y-3">
                    <p className="text-sm font-medium text-white">Compartí este código o link con tus alumnos para que se registren solos:</p>

                    {editingCode ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Input
                                    value={codeInput}
                                    onChange={e => setCodeInput(e.target.value)}
                                    placeholder="nuevo-codigo"
                                    className="flex-1 h-9 font-mono bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40"
                                />
                                <Button size="sm" onClick={saveInviteCode} disabled={savingCode}>
                                    {savingCode ? 'Guardando...' : 'Guardar'}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => { setEditingCode(false); setCodeError(''); }} className="border-[#3a3a3f] text-white hover:bg-white/5 hover:text-white">
                                    Cancelar
                                </Button>
                            </div>
                            <p className="text-xs text-white/40">Letras minúsculas, números y guiones. Mínimo 4 caracteres.</p>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center h-9 px-3 rounded-md border border-[#3a3a3f] bg-black font-mono text-sm font-bold tracking-[0.2em] uppercase text-white">
                                {inviteCode}
                            </span>
                            <Button variant="outline" size="sm" onClick={() => copyText(inviteCode, setCopiedCode)} className="border-[#3a3a3f] text-white hover:bg-white/5 hover:text-white">
                                {copiedCode ? <><CheckCircle2 className="h-4 w-4 mr-1 text-white" />Copiado</> : <><Copy className="h-4 w-4 mr-1" />Copiar código</>}
                            </Button>
                            <Button variant="outline" size="sm" onClick={startEditCode} className="border-[#3a3a3f] text-white hover:bg-white/5 hover:text-white">
                                <Pencil className="h-4 w-4 mr-1" />Editar
                            </Button>
                            <Button variant="outline" size="sm" onClick={regenerateInvite} disabled={generatingLink} className="border-[#3a3a3f] text-white hover:bg-white/5 hover:text-white">
                                <RefreshCw className={`h-4 w-4 mr-1 ${generatingLink ? 'animate-spin' : ''}`} />Regenerar
                            </Button>
                        </div>
                    )}

                    {codeError && <p className="text-xs text-red-400">{codeError}</p>}

                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            readOnly
                            value={inviteUrl}
                            className="flex-1 h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm font-mono text-white/60"
                        />
                        <Button variant="outline" size="sm" onClick={() => copyText(inviteUrl, setCopied)} className="border-[#3a3a3f] text-white hover:bg-white/5 hover:text-white">
                            {copied ? <><CheckCircle2 className="h-4 w-4 mr-1 text-white" />Copiado</> : <><Copy className="h-4 w-4 mr-1" />Copiar link</>}
                        </Button>
                    </div>
                </div>
            )}

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input placeholder="Buscar por nombre o DNI..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                    </div>
                </CardHeader>
                <CardContent>
                    {listError && (
                        <div className="bg-white/5 text-white text-sm p-3 rounded-md mb-4">{listError}</div>
                    )}
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : customers.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron alumnos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm md:min-w-[600px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">DNI</th>
                                            <th className="hidden md:table-cell text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Teléfono</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Email</th>
                                            <th className="hidden md:table-cell text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Cuenta</th>
                                            <th className="hidden md:table-cell text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Tarjeta</th>
                                            <th className="text-right py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customers.map(c => (
                                            <tr key={c.id} className="border-b border-[#3a3a3f] last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-medium text-white max-md:truncate max-md:max-w-[150px]">{c.first_name} {c.last_name}</td>
                                                <td className="py-3 px-2 font-mono text-white/60">{c.dni}</td>
                                                <td className="hidden md:table-cell py-3 px-2 text-white/60">{c.phone || '-'}</td>
                                                <td className="py-3 px-2 text-white/60 max-md:truncate max-md:max-w-[150px]">{c.email || '-'}</td>
                                                <td className="hidden md:table-cell py-3 px-2">
                                                    {c.user_id ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-black">Activa</span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white">Sin cuenta</span>
                                                    )}
                                                </td>
                                                <td className="hidden md:table-cell py-3 px-2">
                                                    {c.active_card ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-black">Activa</span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white">Sin tarjeta</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {!c.user_id && (
                                                            <Button variant="ghost" size="icon" onClick={() => handleCreateAccount(c)} title="Crear cuenta" className="text-white hover:bg-white/5"><UserCircle className="h-4 w-4" /></Button>
                                                        )}
                                                        <Button variant="ghost" size="icon" onClick={() => navigate(`/panel/mediciones?customer=${c.id}`)} title="Mediciones" className="text-white hover:bg-white/5"><HeartPulse className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} className="text-white hover:bg-white/5"><Pencil className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="text-white hover:bg-white/5"><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{editing ? 'Editar Alumno' : 'Nuevo Alumno'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Nombre</Label>
                                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Apellido</Label>
                                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">DNI</Label>
                                <Input value={form.dni} onChange={e => setForm({ ...form, dni: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Teléfono</Label>
                                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Email</Label>
                                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha de nacimiento</Label>
                                <Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Notas</Label>
                            <textarea
                                className="flex min-h-[60px] w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>
                        {!editing && (
                            <div className="space-y-3 border-t border-[#3a3a3f] pt-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="rounded border-[#3a3a3f] bg-[#0a0a0a] text-white focus:ring-white"
                                        checked={createAccount}
                                        onChange={e => setCreateAccount(e.target.checked)}
                                    />
                                    <span className="text-sm text-white">Crear cuenta de acceso para el alumno</span>
                                </label>
                                {createAccount && (
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Contraseña de la cuenta</Label>
                                        <Input
                                            type="password"
                                            placeholder="Mínimo 8 caracteres"
                                            value={accountPassword}
                                            onChange={e => setAccountPassword(e.target.value)}
                                            minLength={8}
                                            required={createAccount}
                                            className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
